import { ApiResult, caller, urlMaker } from '@shared/utils';
import { IBranchPartner, IBranchPartnerFormValues, IBranchPartnerPersistence } from '../interfaces';
import { BranchPartnersMapper, CreateBranchPartnerErrorsMapper, CreateBranchPartnerMapper } from '../mappers';

const appendToFormData = (body: FormData, payload: Record<string, string | number | undefined>) => {
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined) body.append(key, String(value));
  });
};

export class BranchPartnersService {
  public static async getList(query: Record<string, any> = {}): Promise<ApiResult<200, { data: IBranchPartner[]; total: number }> | ApiResult<400 | 500, string>> {
    const url = urlMaker('/api/admin/companies', query);
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        const data: IBranchPartner[] = (result.data || []).map((item: IBranchPartnerPersistence) => BranchPartnersMapper.toDomain(item));
        return new ApiResult(200, { data, total: result.total }, null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi', null);
    }
  }

  public static async getById(id: string | number): Promise<ApiResult<200, IBranchPartner> | ApiResult<400 | 500, string>> {
    const url = urlMaker('/api/admin/companies/info', { company_id: id });
    try {
      const response = await caller(url);
      if (response.ok) {
        const result = await response.json();
        return new ApiResult(200, BranchPartnersMapper.toDomain(result.data), null);
      }
      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  }

  /** Creates when `id` is absent, edits when present — matches the old single-repo behaviour. */
  public static async save(values: IBranchPartnerFormValues): Promise<ApiResult<200, null> | ApiResult<422, Record<string, string>> | ApiResult<400 | 500, string>> {
    const url = values.id ? urlMaker('/api/admin/companies/edit') : urlMaker('/api/admin/companies/create');
    const body = new FormData();
    appendToFormData(body, CreateBranchPartnerMapper.toPersistence(values));

    try {
      const response = await caller(url, { method: 'POST', body });

      if (response.ok) {
        return new ApiResult(200, null, null);
      }

      if (response.status === 400) {
        const result = await response.json();
        return new ApiResult(422, CreateBranchPartnerErrorsMapper.toDomain(result.errors), null);
      }

      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  }

  public static async delete(id: string | number): Promise<ApiResult<200, null> | ApiResult<400 | 500, string>> {
    const url = urlMaker('/api/admin/companies/delete', { company_id: id });
    try {
      const response = await caller(url);

      if (response.ok) {
        return new ApiResult(200, null, null);
      }

      if (response.status === 400 || response.status === 422) {
        const result = await response.json();
        return new ApiResult(
          400,
          Object.values(result.errors as Record<string, string[]>)
            .flat()
            .join('. '),
          null,
        );
      }

      return new ApiResult(400, 'Məlumatlar əldə edilə bilmədi', null);
    } catch {
      return new ApiResult(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null);
    }
  }
}
